/**
 * Work & Engagement Time Entry Templates
 * General work activities: development, client interactions, support, training,
 * planning, documentation, internal meetings, and administrative tasks.
 */

import type { TimeEntryTemplateData } from "./types";

export const WORK_TEMPLATES = [
  {
    id: "development",
    name: "Development Work",
    description: "Standard development time entry template",
    category: "Development",
    fields: {
      description: "Development work on feature implementation",
      type: "billable",
      billable_rate: 125,
      tags: ["development", "coding"],
    },
  },
  {
    id: "client-meeting",
    name: "Client Meeting",
    description: "Time spent in client meetings and discussions",
    category: "Client Relations",
    fields: {
      description: "Client meeting and consultation",
      type: "billable",
      billable_rate: 150,
      tags: ["meeting", "client", "consultation"],
    },
  },
  {
    id: "support-call",
    name: "Support Call",
    description: "Technical support and troubleshooting",
    category: "Support",
    fields: {
      description: "Technical support and issue resolution",
      type: "billable",
      billable_rate: 100,
      tags: ["support", "troubleshooting", "helpdesk"],
    },
  },
  {
    id: "training",
    name: "Training Session",
    description: "Training and knowledge transfer sessions",
    category: "Training",
    fields: {
      description: "Training session and knowledge transfer",
      type: "billable",
      billable_rate: 125,
      tags: ["training", "education", "knowledge-transfer"],
    },
  },
  {
    id: "research",
    name: "Research & Planning",
    description: "Research, planning, and documentation work",
    category: "Planning",
    fields: {
      description: "Research and planning activities",
      type: "research",
      billable_rate: 100,
      tags: ["research", "planning", "documentation"],
    },
  },
  {
    id: "documentation",
    name: "Documentation",
    description: "Creating and updating documentation",
    category: "Documentation",
    fields: {
      description: "Documentation creation and updates",
      type: "billable",
      billable_rate: 100,
      tags: ["documentation", "writing", "technical-writing"],
    },
  },
  {
    id: "internal-meeting",
    name: "Internal Meeting",
    description: "Internal team meetings and collaboration",
    category: "Internal",
    fields: {
      description: "Internal team meeting",
      type: "non_billable",
      billable_rate: 0,
      tags: ["meeting", "internal", "collaboration"],
    },
  },
  {
    id: "admin-work",
    name: "Administrative Work",
    description: "Administrative tasks and overhead work",
    category: "Administrative",
    fields: {
      description: "Administrative tasks and overhead",
      type: "non_billable",
      billable_rate: 0,
      tags: ["admin", "overhead", "administrative"],
    },
  },
  {
    id: "client-progress-review",
    name: "Client Progress Review",
    description: "Followed up with company contact and reviewed progress",
    category: "Client Relations",
    fields: {
      description: "Followed up with {{contact_name}} and reviewed progress on {{project_name}}",
      type: "billable",
      billable_rate: 150,
      tags: ["client", "progress", "review", "follow-up"],
    },
  },
  {
    id: "user-training",
    name: "User Training",
    description: "Provided training to end users on systems or software",
    category: "Training",
    fields: {
      description: "User training session for {{user_name}} on {{topic}}",
      type: "billable",
      billable_rate: 125,
      tags: ["training", "user", "education", "knowledge-transfer"],
    },
  },
] as const satisfies readonly TimeEntryTemplateData[];
